import React from "react"
import { graphql } from "gatsby"

import Layout from "../page/Layout"
import SEO from "../page/Seo"
import Sections from "../page/Sections"
import { PopupsProvider } from "../../contexts/PopupsContext"
import {
  normalize,
  normalizeSections,
  findHeaderByPath,
  findFooterByPath,
} from "../../utils/utils"

const getFitHeight = value => {
  const num = Number(value ?? 100)
  const pct = Number.isFinite(num) ? Math.min(Math.max(num, 0), 100) : 100

  return `${pct}vh`
}

const Page = ({ data, masterPageURL, siteUrl, pagePath }) => {
  const normalizedData = normalize(data)

  const site = normalizedData.strapiSite
  const page = normalizedData.strapiPage || {}
  const { header: pageHeader } = findHeaderByPath(site.header, page?.pageHeader)
  const header = pageHeader ?? site.header[0]
  const { footer: pageFooter } = findFooterByPath(site.footer, page?.pageFooter)
  const footer = pageFooter ?? site.footer[0]
  const SSR_COUNT = 3 // kept for eagerLoad hints; all sections render in initial HTML
  page.sections = normalizeSections(page?.sections || [])

  // change first section to h1, and the rest to h2 but keep client style choice
  if (page.sections) {
    page.sections.map((section, index) => {
      const textObjName = section.text ? "text" : "sectionTitle"
      const textObject = section[`${textObjName}`]
      if (index === 0 && textObject) {
        textObject.as = "h1"
        section[`${textObjName}`] = textObject
      } else if (index !== 0 && textObject) {
        textObject.as = "h2"
        section[`${textObjName}`] = textObject
      }
      return section
    })
  }

  const firstSectionHasParallax =
    page?.sections &&
    page?.sections[0]?.section?.parallaxOptions !== "noParallax" &&
    page?.sections[0]?.section?.parallaxOptions !== undefined

  const htmlSnippets = page?.htmlSnippets
  const resolvedPagePath = pagePath || page?.slug || "/"
  const cleanPagePath = String(resolvedPagePath).replace(/^\/+|\/+$/g, "")
  const isHomepage =
    resolvedPagePath === "/" || cleanPagePath === "" || cleanPagePath === "home"

  let pageSpecificNavTheme =
    page?.hideHeader !== "yes"
      ? {
          overrideSiteStyles: page?.overrideSiteStyles,
          transparentNav: page?.transparentNav,
          pageNavTheme: page?.pageNavTheme,
          bgNav: page?.bgNav,
        }
      : {}

  const isStackedNav = header.layout === "nav-stacked"
  const headerHeight = isStackedNav ? 130 : 80
  const isTransparentNav =
    page?.transparentNav && page?.hideHeader !== "yes" ? true : false

  const transNavFirstSectionStyles = {
    margin: page?.transparentNav
      ? `-${headerHeight}px auto 0px auto !important`
      : null,
    left: "0px",
    right: "0px",
    pt: !firstSectionHasParallax ? `${headerHeight}px` : "0px",
  }
  let firstSectionStyling = {
    theme: "default",
    bg: 0,
  }
  let lastSectionStyling = {
    theme: "default",
    bg: 0,
  }

  const visibleSectionsWithStyles = page?.sections?.filter(
    currSection => currSection.section && !currSection.section.hidden,
  )
  const firstSection = visibleSectionsWithStyles && visibleSectionsWithStyles[0]
  const lastSection =
    visibleSectionsWithStyles &&
    visibleSectionsWithStyles[visibleSectionsWithStyles.length - 1]

  if (firstSection) {
    firstSectionStyling.theme = firstSection.section.theme
    firstSectionStyling.bg = firstSection.section.bg
    firstSectionStyling.sectionBreak = firstSection.section.sectionBreak
  }
  if (lastSection) {
    lastSectionStyling.theme = lastSection.section.theme
    lastSectionStyling.bg = lastSection.section.bg
    lastSectionStyling.sectionBreak = lastSection.section.sectionBreak
  }

  let prevSection = null
  let visibleSections = page?.sections?.filter(sec => !sec.section?.hidden)

  const footerStyle = {}

  return (
    <PopupsProvider>
      <Layout
        header={header}
        footer={footer}
        site={site}
        firstSectionStyling={firstSectionStyling}
        lastSectionStyling={lastSectionStyling}
        isTransparentNav={isTransparentNav}
        bgOpacityNav={page.bgOpacityNav}
        pageSpecificNavTheme={pageSpecificNavTheme}
        footerStyle={footerStyle}
        isHomepage={isHomepage}
        htmlSnippets={htmlSnippets?.filter(
          snippet => snippet.dataObj?.htmlDetails?.location === "body",
        )}
      >
        <SEO
          socialShare={
            page.socialShareImage
              ? page.socialShareImage
              : site.socialShareImage
          }
          showSiteTitleInPageTitle={site.showSiteTitleInPageTitle}
          siteTitleLocationInPageTitle={site.siteTitleLocationInPageTitle}
          enableJsonLdGeneration={site.enableJsonLdGeneration}
          favicon={site.favicon}
          metaDescription={site.siteDescription}
          siteTitle={site.title}
          title={page.metaTitle || page.title}
          keywords={page.keywords}
          pageDescription={page.pageDescription}
          canonicalLink={masterPageURL}
          siteUrl={siteUrl}
          pagePath={resolvedPagePath}
          pageSections={visibleSections}
          headerData={header}
          footerData={footer}
          htmlSnippets={htmlSnippets?.filter(
            snippet => snippet.dataObj?.htmlDetails?.location === "head",
          )}
        />

        {visibleSections?.map((section, i) => {
          const key = `${section.__typename}_${section.id}`
          const lastSectionIndex = visibleSections.length - 1
          prevSection = visibleSections[i - 1]?.section

          const isFirstSection = i === 0
          const lastSectionHasBreak =
            section?.section?.sectionBreak === "slope" && i === lastSectionIndex
          const prevSectionHasBreak =
            !isFirstSection &&
            prevSection &&
            prevSection?.sectionBreak === "slope"

          const transNavSectionStyling =
            isFirstSection && isTransparentNav ? transNavFirstSectionStyles : {}

          const isCustomHtml =
            section.__component === "sections.custom-html" ||
            section.__typename === "STRAPI__COMPONENT_SECTIONS_CUSTOM_HTML"

          const sectionPreviewStyling = {
            zIndex:
              isCustomHtml && i === lastSectionIndex
                ? 51 /* setting zIndex above footer (50) but below header (60)*/
                : lastSectionIndex - i + 1,
            mt: prevSectionHasBreak ? "-81px !important" : null,
            pt: prevSectionHasBreak ? `calc(81px + 80px) !important` : null,
            pb: section?.section?.sectionBreak ? `80px` : null,
            mb: lastSectionHasBreak ? "80px !important" : null,
            overflow: isCustomHtml ? "visible" : undefined,
            ...transNavSectionStyling,
          }

          const isFullHeight =
            section?.section?.verticalPadding === "fullHeight"
          const fitHeight = getFitHeight(section?.section?.fullHeightPercentage)
          const fitHeightFull = `${fitHeight} !important`
          const fitHeightMinusNav = `calc(${fitHeight} - ${headerHeight}px) !important`
          const contentStyles =
            isFirstSection && firstSectionHasParallax && isTransparentNav
              ? { pt: `${headerHeight}px` }
              : undefined

          return (
            <Sections
              key={key}
              section={{
                ...section,
                section: {
                  ...section.section,
                  prevSection: prevSection,
                  isFullHeight: isFullHeight,
                  ...sectionPreviewStyling,
                  minHeight: {
                    _:
                      isFullHeight && isFirstSection && isTransparentNav
                        ? fitHeightMinusNav // needed so the transparent nav overlaps the first section
                        : isFullHeight
                          ? fitHeightFull
                          : null,
                    md: isFullHeight ? fitHeightFull : null,
                  },
                  contentStyles: contentStyles,
                  navHeight: headerHeight,
                  isFirstSection,
                  isTransparentNav,
                  isNavHidden: header?.headerVisibility === "hidden",
                },
              }}
              index={i}
              ssrCount={SSR_COUNT}
            />
          )
        })}
      </Layout>
    </PopupsProvider>
  )
}

export default Page

export const query = graphql`
  fragment Page on STRAPI_PAGE {
    strapi_id
    title
    slug
    redirectType
    redirectURL
    metaTitle
    masterCopyPageId
    transparentNav
    bgOpacityNav
    overrideSiteStyles
    pageNavTheme
    bgNav
    pageHeader
    pageFooter
    hideHeader
    hideFooter
    metaTitle
    keywords
    pageDescription
    socialShareImage {
      strapi_id
      url
      alternativeText
      height
      width
      localFile {
        childImageSharp {
          gatsbyImageData(
            formats: [AUTO, WEBP, AVIF]
            layout: CONSTRAINED
            width: 500
          )
        }
      }
    }
    sections {
      ...Sections
    }
    htmlSnippets {
      dataObj: data
      strapi_id
      label
      provider
      providerName
      type
    }
  }
`
// onEnter and preloadQuery functions are meant to improve load time performance
export const preloadQuery = () => {
  return [
    {
      query: query,
      prefetch: true, // This option prefetches the query result before it's actually needed.
    },
  ]
}

export const onEnter = () => {
  // This function tells Gatsby to preload the query as soon as the component is about to enter the viewport
  if (typeof window !== "undefined") {
    window.___gatsbyPrefetchQuery(preloadQuery)
  }
}
